import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { makeAirPayment } from '../../api/endpoints/air';
import type { PaymentForm as PaymentFormValues, PaymentType } from '../../api/types/air';
import { getShoppingCart } from '../../api/endpoints/shoppingCart';
import { createInsuranceOffer, createInsurancePolicy } from '../../api/endpoints/insurance';
import { ApiError } from '../../api/types/common';
import { redirectToBankIfNeeded } from '../../components/payment/paymentRedirect';
import { PaymentForm } from '../../components/payment/PaymentForm';
import { useAuthStore } from '../../auth/authStore';
import { buildInsuranceRequestsFromCart, readCartInsurance } from '../air/insurance';

const PENDING_KEY = (cartId: string) => `air:pendingInsurance:${cartId}`;
const LEG_KEY = (cartId: string) => `air:pendingInsuranceLeg:${cartId}`;

function clearPending(cartId: string) {
  try {
    sessionStorage.removeItem(PENDING_KEY(cartId));
  } catch {
    /* storage kapalı */
  }
}

/**
 * `/payment/return` üzerinde 3DS uçuş ödemesinden sonra sigortayı tamamlayan adım — akış
 * içi `AirInsurancePanel` + `runInsuranceChain`'in (flowConfig.ts) dönüş-sayfası karşılığı.
 * Banka dönüşünde in-memory flow yok olduğu için sepetten yeniden kurulur: `CreateOffer` →
 * ikinci `MakePayment` (sigorta `TotalFare`) → `CreatePolicy`. Sigorta ödemesi de 3DS ise
 * `air:pendingInsuranceLeg` bayrağı bırakılıp bankaya yönlendirilir; dönüşte `PaymentReturnPage`
 * yalnız `CreatePolicy` çağırır. Best-effort: her hata uyarıyla biter, uçuş biletli kalır.
 */
export function AirInsuranceReturnPanel({
  cartId,
  onDone,
  onSkip,
}: {
  cartId: string;
  onDone: (teklifId: string) => void;
  onSkip: () => void;
}) {
  const currentUser = useAuthStore((s) => s.currentUser);
  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const offerQuery = useQuery({
    queryKey: ['returnInsuranceOffer', cartId],
    queryFn: async () => {
      const cart = (await getShoppingCart(cartId)).shoppingCart;
      const reqs = buildInsuranceRequestsFromCart(cart);
      if (!reqs) throw new Error('Sigorta için yolcu/uçuş bilgisi eksik.');
      const offer = await createInsuranceOffer({ ...reqs.createOffer, shoppingCartId: cartId });
      const teklifId = offer.data?.teklifBilgileri?.teklifId;
      if (!teklifId) {
        throw new Error(offer.data?.errorMessage || 'Sigorta teklifi oluşturulamadı.');
      }
      const fresh = (await getShoppingCart(cartId)).shoppingCart;
      const insTotal =
        readCartInsurance(fresh).totalFare ?? offer.data?.teklifBilgileri?.fiyatFloat ?? null;
      if (insTotal == null) throw new Error('Sigorta tutarı belirlenemedi.');
      return { teklifId: String(teklifId), insTotal };
    },
    retry: false,
    staleTime: Infinity,
  });

  const handlePay = async ({
    paymentForm,
    paymentType,
  }: {
    paymentForm: PaymentFormValues | undefined;
    paymentType: PaymentType;
  }) => {
    if (!offerQuery.data) return;
    const { teklifId, insTotal } = offerQuery.data;
    setBusy(true);
    setPayError(null);
    try {
      const res = await makeAirPayment({
        shoppingCartId: cartId,
        amount: insTotal,
        paymentType,
        paymentForm: paymentType === 'CurrentPayment' ? undefined : paymentForm,
      });
      // Sigorta ödemesi de 3DS — bankaya git, dönüşte yalnız CreatePolicy.
      if (redirectToBankIfNeeded(res)) {
        try {
          sessionStorage.setItem(LEG_KEY(cartId), teklifId);
        } catch {
          /* storage kapalı */
        }
        return;
      }
      const fresh = (await getShoppingCart(cartId)).shoppingCart;
      if (!fresh) throw new Error('Sepet alınamadı.');
      await createInsurancePolicy({ teklifId, shoppingCart: fresh });
      clearPending(cartId);
      onDone(teklifId);
    } catch (err) {
      setPayError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Sigorta ödemesi tamamlanamadı.'
      );
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, width: '100%' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Seyahat Sağlık Sigortası (Tamamlıyo)
      </Typography>

      {offerQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {offerQuery.isError && (
        <Stack spacing={2}>
          <Alert severity="warning">
            Uçuşunuz tamamlandı; sigorta poliçesi şu an oluşturulamadı
            {offerQuery.error instanceof Error ? ` (${offerQuery.error.message})` : ''}.
          </Alert>
          <Box>
            <Button
              variant="contained"
              onClick={() => {
                clearPending(cartId);
                onSkip();
              }}
            >
              Rezervasyon Listesine Git
            </Button>
          </Box>
        </Stack>
      )}

      {offerQuery.data && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Uçuş ödemeniz alındı. Poliçe ücreti{' '}
            <strong>{offerQuery.data.insTotal.toLocaleString('tr-TR')} TRY</strong> ayrıca tahsil
            edilir.
          </Typography>
          {payError && <Alert severity="error">{payError}</Alert>}
          <PaymentForm
            amount={offerQuery.data.insTotal}
            currency="TRY"
            loading={busy}
            shoppingCartId={cartId}
            creditCardPaymentAllowed={currentUser?.creditCardPayment ?? true}
            threeDPaymentAllowed={!!currentUser?.threeDPayment}
            submitLabel="Sigorta Ödemesini Tamamla"
            onSubmit={handlePay}
          />
          <Box>
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() => {
                clearPending(cartId);
                onSkip();
              }}
            >
              Sigortadan Vazgeç
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
